import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Loader2, XCircle, Sparkles } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PaymentSuccessPage() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const kind = searchParams.get("kind"); // "membership" for subscription checkout
  const isMembership = kind === "membership";
  const { refreshUser } = useAuth();
  const [state, setState] = useState("checking");
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) { setState("error"); return; }
    let timer;
    const poll = async () => {
      attempts.current += 1;
      if (attempts.current > 10) { setState("timeout"); return; }
      try {
        const r = await axios.get(`${API}/payments/status/${sessionId}`);
        if (r.data.payment_status === "paid") {
          setState("paid");
          if (isMembership && refreshUser) { refreshUser(); }
          return;
        }
        if (["failed", "expired"].includes(r.data.payment_status)) { setState("failed"); return; }
        timer = setTimeout(poll, 2000);
      } catch {
        timer = setTimeout(poll, 2000);
      }
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId, isMembership, refreshUser]);

  return (
    <main data-testid="payment-success-page" className="max-w-lg mx-auto px-4 py-24 text-center">
      {state === "checking" && (
        <div data-testid="payment-checking">
          <Loader2 className="w-12 h-12 text-emerald-600 animate-spin mx-auto mb-6" />
          <h1 className="font-heading text-3xl tracking-tight mb-3">Confirming your payment…</h1>
          <p className="text-slate-500">Hold on a moment while we verify with Stripe.</p>
        </div>
      )}
      {state === "paid" && (
        <div data-testid="payment-paid">
          {isMembership ? <Sparkles className="w-14 h-14 text-emerald-600 mx-auto mb-6" /> : <CheckCircle2 className="w-14 h-14 text-emerald-600 mx-auto mb-6" />}
          <h1 className="font-heading text-3xl md:text-4xl tracking-tight mb-3">
            {isMembership ? "Welcome to the Members Club 🎉" : "You're featured! 🎉"}
          </h1>
          <p className="text-slate-600 leading-relaxed mb-8">
            {isMembership
              ? "Your $1.99/mo membership is active. Every exclusive high-profit affiliate link is now unlocked, and your personal tracked share link is ready in the Official Links directory."
              : "Payment confirmed. Our team will review your submission and activate your featured placement within 24 hours. A confirmation has been sent to your email."}
          </p>
          <Link
            to={isMembership ? "/official-links" : "/programs"}
            data-testid="payment-success-browse-link"
            className="inline-block px-8 py-3.5 rounded-full bg-slate-900 text-white font-medium hover:bg-slate-700 transition-colors duration-200"
          >
            {isMembership ? "Open exclusive links" : "Back to directory"}
          </Link>
        </div>
      )}
      {(state === "failed" || state === "error") && (
        <div data-testid="payment-failed">
          <XCircle className="w-14 h-14 text-rose-500 mx-auto mb-6" />
          <h1 className="font-heading text-3xl tracking-tight mb-3">Payment not completed</h1>
          <p className="text-slate-600 mb-8">Your payment didn't go through. No charge was made.</p>
          <Link to={isMembership ? "/membership" : "/get-featured"} className="inline-block px-8 py-3.5 rounded-full bg-slate-900 text-white font-medium hover:bg-slate-700 transition-colors duration-200">Try again</Link>
        </div>
      )}
      {state === "timeout" && (
        <div data-testid="payment-timeout">
          <Loader2 className="w-12 h-12 text-slate-400 mx-auto mb-6" />
          <h1 className="font-heading text-3xl tracking-tight mb-3">Still processing…</h1>
          <p className="text-slate-600 mb-8">Your payment is taking longer than usual. If you were charged, your listing will activate automatically — or contact us via the About page.</p>
          <Link to="/about" className="inline-block px-8 py-3.5 rounded-full bg-slate-900 text-white font-medium hover:bg-slate-700 transition-colors duration-200">Contact us</Link>
        </div>
      )}
    </main>
  );
}
